import { memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, forwardRef } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import styles from '../V2OnboardingAgent.module.css';
import dashStyles from '../../Dashboard.module.css';
import BrandLogo from '../../BrandLogo';
import { formatResponse, getUserInitials, trimMessageContentHtml } from '../../../utils/helper';
import logoImage from '../../../assets/logo.png';
import { logger } from '../../../config';
import {
  INPUT_MAX_LEN,
  appendTypingCursorToFormattedHtml,
  cx,
  formatAgentBubbleHtml,
  formatRelativeTime,
  joinPrefixWithTranscript,
} from './helpers';

const TYPING_WORD_MAX = 24;
const USER_AVATAR_SRC = `${process.env.PUBLIC_URL || ''}/user.png`;

function randomBetween(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function prefersReducedMotion() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Reveal whole words (plus their trailing space) per step so text streams in fluidly
 * instead of one character at a time. Very long tokens are capped at {@link TYPING_WORD_MAX}.
 */
function getTypingChunkSize(graphemes, startIdx) {
  const max = graphemes.length - startIdx;
  if (max <= 0) return 0;

  let end = startIdx;

  while (end < graphemes.length && (graphemes[end] === ' ' || graphemes[end] === '\n')) {
    end += 1;
    if (end - startIdx >= TYPING_WORD_MAX) return end - startIdx;
  }

  while (end < graphemes.length && graphemes[end] !== ' ' && graphemes[end] !== '\n') {
    end += 1;
    if (end - startIdx >= TYPING_WORD_MAX) break;
  }

  if (graphemes[end] === ' ') end += 1;

  const chunk = end - startIdx;
  return chunk > 0 ? Math.min(max, chunk) : Math.min(max, 1);
}

function getTypingStepDelay(graphemes, lastWrittenIndex) {
  if (lastWrittenIndex < 0) return randomBetween(160, 260);
  const c = graphemes[lastWrittenIndex];
  const prev = lastWrittenIndex > 0 ? graphemes[lastWrittenIndex - 1] : '';

  if (c === '.' || c === '!' || c === '?' || prev === '.' || prev === '!' || prev === '?') {
    return randomBetween(240, 400);
  }
  if (c === '\n' || prev === '\n') return randomBetween(180, 300);
  if (c === ',' || c === ';' || c === ':' || prev === ',' || prev === ';' || prev === ':') {
    return randomBetween(110, 190);
  }
  return randomBetween(55, 110);
}

function AgentAvatar() {
  return (
    <BrandLogo
      src={logoImage}
      decorative
      decoding="async"
      className={styles['agent-avatar']}
    />
  );
}

const V2AgentMessageRow = memo(function V2AgentMessageRow({ message, messageIndex, onTypingComplete, relativeTimeTick }) {
  const fullRaw = String(message.content ?? '');
  const graphemes = useMemo(() => [...fullRaw], [fullRaw]);
  const shouldAnimate = Boolean(message.animateTyping) && !message.isHtml;
  const reduceMotion = prefersReducedMotion();
  const timestampLabel = formatRelativeTime(message.ts || message.timestamp);

  const [visibleCount, setVisibleCount] = useState(() => {
    if (!shouldAnimate || reduceMotion) return graphemes.length;
    return 0;
  });

  useEffect(() => {
    if (!shouldAnimate || reduceMotion) {
      setVisibleCount(graphemes.length);
      return undefined;
    }
    if (graphemes.length === 0) {
      setVisibleCount(0);
      return undefined;
    }

    setVisibleCount(0);
    let idx = 0;
    let cancelled = false;
    let timerId;

    const runStep = () => {
      if (cancelled) return;
      if (idx >= graphemes.length) return;
      const chunk = getTypingChunkSize(graphemes, idx);
      idx = Math.min(idx + chunk, graphemes.length);
      setVisibleCount(idx);
      if (idx >= graphemes.length) return;
      const delay = getTypingStepDelay(graphemes, idx - 1);
      timerId = window.setTimeout(runStep, delay);
    };

    timerId = window.setTimeout(runStep, randomBetween(220, 380));

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [message.id, shouldAnimate, reduceMotion, graphemes]);

  const typingCompleteFiredRef = useRef(false);

  useEffect(() => {
    typingCompleteFiredRef.current = false;
  }, [message.id]);

  useEffect(() => {
    if (typingCompleteFiredRef.current || !onTypingComplete) return;
    if (graphemes.length === 0) return;

    const finished = visibleCount >= graphemes.length;
    if (!finished) return;

    typingCompleteFiredRef.current = true;
    onTypingComplete();
  }, [visibleCount, graphemes.length, onTypingComplete]);

  const visibleSlice = graphemes.slice(0, visibleCount).join('');
  let html = message.isHtml
    ? trimMessageContentHtml(message.content)
    : formatAgentBubbleHtml(visibleSlice);

  const isTyping = shouldAnimate && visibleCount < graphemes.length;
  if (isTyping && html) {
    html = appendTypingCursorToFormattedHtml(html, styles['v2-typing-cursor']);
  }

  return (
    <div className={styles['message-container']} style={{ '--message-index': messageIndex }}>
      <div className={cx(styles['agent-message'], isTyping && styles['agent-message--typing'])}>
        <AgentAvatar />
        <div
          className={cx(
            styles['message-bubble'],
            styles['question-bubble'],
            styles['v2-question-bubble'],
            isTyping && styles['v2-question-bubble--typing']
          )}
          aria-busy={isTyping}
        >
          {isTyping ? <div className={styles['v2-typing-shimmer']} aria-hidden /> : null}
          <div
            className={styles['message-content']}
            data-typing={isTyping ? 'true' : undefined}
            dangerouslySetInnerHTML={{ __html: html }}
          />
          <div className={styles['message-timestamp']}>{timestampLabel}</div>
        </div>
      </div>
    </div>
  );
});

export function V2TypingDots({ messageIndex }) {
  return (
    <div
      className={styles['message-container']}
      style={{ '--message-index': messageIndex }}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Building your forecast"
    >
      <div className={cx(styles['agent-message'], styles['agent-message--typing'])} aria-hidden="true">
        <AgentAvatar />
        <div
          className={cx(
            styles['message-bubble'],
            styles['question-bubble'],
            styles['v2-question-bubble'],
            styles['v2-typing-dots-bubble']
          )}
        >
          <span className={styles['v2-typing-dots-label']}>Building your forecast</span>
          <span className={styles['v2-typing-dots']}>
            <span className={styles['v2-typing-dot']} />
            <span className={styles['v2-typing-dot']} />
            <span className={styles['v2-typing-dot']} />
          </span>
        </div>
      </div>
    </div>
  );
}

const UserMessageRow = memo(function UserMessageRow({ message, messageIndex, userInitials, relativeTimeTick }) {
  const timestampLabel = formatRelativeTime(message.ts || message.timestamp);
  const html = useMemo(
    () => trimMessageContentHtml(formatResponse(message.content)),
    [message.content]
  );

  return (
    <div className={styles['message-container']} style={{ '--message-index': messageIndex }}>
      <div className={styles['user-message']}>
        <div className={styles['user-avatar']}>
          <div className={styles['user-initials']}>{userInitials}</div>
        </div>
        <div className={styles['message-bubble']}>
          <div
            className={styles['message-content']}
            dangerouslySetInnerHTML={{ __html: html }}
          />
          <div className={styles['message-timestamp']}>{timestampLabel}</div>
        </div>
      </div>
    </div>
  );
});

export const V2ChatHeader = memo(function V2ChatHeader({ isCopilotMode, profileCompletionPercent }) {
  const headerInstruction = isCopilotMode
    ? 'Your onboarding is complete — keep chatting to refine your plan or update your profile on the right.'
    : 'Chat with me to build your financial forecast. I will fill in your profile as we go.';

  return (
    <header className={styles['v2-header']}>
      <div className={styles['v2-header-top']}>
        <div className={styles['v2-header-intro']}>
          <BrandLogo
            src={logoImage}
            decorative
            decoding="async"
            className={styles['v2-header-avatar']}
          />
          <div className={styles['v2-header-identity']}>
            <h1 className={cx(styles['v2-header-title'], isCopilotMode && styles['v2-header-title--copilot'])}>
              {isCopilotMode ? 'Reese — Copilot' : 'Reese'}
            </h1>
            <p className={styles['v2-header-status']}>Online</p>
          </div>
        </div>
        <p className={styles['v2-header-instruction']}>{headerInstruction}</p>
      </div>
      <div
        className={styles['v2-header-progress']}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={profileCompletionPercent}
        aria-label="Profile completion"
      >
        <div className={styles['v2-header-progress-row']}>
          <span className={styles['v2-header-progress-label']}>Profile Completion</span>
          <span className={styles['v2-header-progress-value']}>{profileCompletionPercent}%</span>
        </div>
        <div className={styles['v2-header-progress-track']}>
          <div
            className={styles['v2-header-progress-fill']}
            style={{ width: `${profileCompletionPercent}%` }}
          />
        </div>
      </div>
    </header>
  );
});

export function V2LoadingPanel() {
  return (
    <div className={cx(styles['v2-loading-panel'], styles['v2-loading-panel--bootstrap'])}>
      <div className={styles['v2-loading-visual']} aria-hidden>
        <span className={styles['v2-loading-halo']} />
        <span className={styles['v2-loading-spinner']} />
      </div>
      <div className={styles['v2-loading-text-block']}>
        <p className={styles['v2-loading-label']}>Loading…</p>
        <p className={styles['v2-loading-hint']}>Setting up your conversation</p>
      </div>
    </div>
  );
}

export function LocationConsentPopup({ open, onChoice }) {
  if (!open) return null;
  return (
    <div
      className={styles['v2-agent-request-overlay']}
      role="dialog"
      aria-modal="true"
      aria-label="Location detection"
    >
      <div className={styles['v2-loc-consent-card']}>
        <h2 className={styles['v2-loc-consent-title']}>Use your location?</h2>
        <p className={styles['v2-loc-consent-body']}>
          We can use your approximate location to personalize cost-of-living and expense estimates. You can skip this if you prefer.
        </p>
        <div className={styles['v2-loc-consent-actions']}>
          <button
            type="button"
            className={styles['v2-loc-consent-secondary']}
            onClick={() => onChoice(false)}
          >
            Not now
          </button>
          <button
            type="button"
            className={styles['v2-loc-consent-primary']}
            onClick={() => onChoice(true)}
          >
            Yes, detect location
          </button>
        </div>
      </div>
    </div>
  );
}

export function TopAppHeader({ firstName, menuOpen, onToggleMenu, onMenuSelect }) {
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        onMenuSelect(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      onMenuSelect(null);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen, onMenuSelect]);

  return (
    <header className={styles['v2-top-header']} role="banner">
      <div className={styles['v2-top-header-left']}>
        <BrandLogo
          src={logoImage}
          className={styles['v2-top-header-logo-slot']}
          alt="Richuel Logo"
          decoding="async"
        />
        <span className={styles['v2-top-header-brand']}>Richuel</span>
      </div>
      <div className={styles['v2-top-header-right']}>
        <div className={`${styles['v2-top-user-section']} ${dashStyles.userSection}`} ref={userMenuRef}>
          <button
            type="button"
            className={dashStyles.userDropdown}
            onClick={onToggleMenu}
            aria-haspopup="true"
            aria-expanded={menuOpen}
            aria-label={`User menu for ${firstName}`}
          >
            <span className={dashStyles.userAvatar} aria-hidden="true">
              <img
                src={USER_AVATAR_SRC}
                alt=""
                className={dashStyles.userAvatarImage}
                draggable="false"
              />
            </span>
          </button>
          {menuOpen && (
            <div className={dashStyles.dropdownMenu} role="menu" aria-label="User menu">
              <div className={dashStyles.dropdownHeader}>
                <span className={dashStyles.dropdownAvatar} aria-hidden="true">
                  <img src={USER_AVATAR_SRC} alt="" draggable="false" />
                </span>
                <span className={dashStyles.dropdownUserName}>{firstName}</span>
              </div>
              <button
                type="button"
                className={`${dashStyles.dropdownItem} ${dashStyles.dropdownItemLogout}`}
                role="menuitem"
                onClick={() => onMenuSelect('logout')}
              >
                <span className={`${dashStyles.navIcon} ${dashStyles.logout}`} aria-hidden="true" />
                <span>Log out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export function V2ErrorState({ onBackToLogin }) {
  return (
    <div className={styles['error-container']}>
      <div className={styles['error-icon']}>⚠️</div>
      <div className={styles['error-text']}>Failed to load onboarding</div>
      <p className={styles['error-description']}>
        We couldn&apos;t load onboarding questions. Please log in again or contact support.
      </p>
      <button type="button" className={styles['retry-button']} onClick={onBackToLogin}>
        Back to Login
      </button>
    </div>
  );
}

export const V2MessageList = memo(function V2MessageList({
  messages,
  showTypingDots,
  showFinalScreen,
  relativeTimeTick,
  onAgentTypingComplete,
}) {
  const userInitials = useMemo(() => getUserInitials(), []);

  return (
    <>
      {messages.map((m, index) =>
        m.role === 'user' ? (
          <UserMessageRow
            key={m.id}
            message={m}
            messageIndex={index}
            userInitials={userInitials}
            relativeTimeTick={relativeTimeTick}
          />
        ) : (
          <V2AgentMessageRow
            key={m.id}
            message={m}
            messageIndex={index}
            relativeTimeTick={relativeTimeTick}
            onTypingComplete={
              m.focusComposerAfter ? () => onAgentTypingComplete(m.id) : undefined
            }
          />
        )
      )}
      {showTypingDots && !showFinalScreen ? (
        <V2TypingDots messageIndex={messages.length} />
      ) : null}
    </>
  );
});

function ListeningIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2" y="5" width="20" height="14" rx="2" ry="2" />
      <path d="M6 9h.01M10 9h.01M14 9h.01M18 9h.01M8 13h.01M12 13h.01M16 13h.01M7 17h10" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3z" />
      <path d="M19 11a7 7 0 01-14 0M12 18v3M8 22h8" />
    </svg>
  );
}

export const V2Composer = forwardRef(function V2Composer(
  { composerLocked, saving, showFinalScreen, onSubmitText, onViewResults, onVoiceEvent },
  ref
) {
  const [inputValue, setInputValue] = useState('');
  const composerInputRef = useRef(null);
  const speechPrefixRef = useRef('');
  const speechCommitInFlightRef = useRef(false);
  const wasListeningRef = useRef(false);
  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();
  const transcriptRef = useRef(transcript);
  transcriptRef.current = transcript;

  const focusInput = useCallback(() => {
    if (composerLocked || listening) return;
    const el = composerInputRef.current;
    if (!el || el.disabled || el.readOnly) return;
    requestAnimationFrame(() => {
      try {
        el.focus({ preventScroll: true });
        const len = el.value.length;
        el.setSelectionRange(len, len);
      } catch {
        el.focus();
      }
    });
  }, [composerLocked, listening]);

  useImperativeHandle(ref, () => ({ focus: focusInput, listening }), [focusInput, listening]);

  useEffect(() => () => {
    SpeechRecognition.stopListening();
  }, []);

  useEffect(() => {
    if (wasListeningRef.current && !listening) focusInput();
    wasListeningRef.current = listening;
  }, [listening, focusInput]);

  const composerDisplayValue = listening
    ? joinPrefixWithTranscript(speechPrefixRef.current, transcript)
    : inputValue;
  const composerSubmitValue = listening
    ? joinPrefixWithTranscript(speechPrefixRef.current, transcriptRef.current)
    : inputValue;
  const canSendComposer = Boolean(composerSubmitValue.trim()) && !saving && !composerLocked;

  useLayoutEffect(() => {
    const target = composerInputRef.current;
    if (!target) return;
    try {
      target.style.height = 'auto';
      target.style.height = `${target.scrollHeight}px`;
    } catch {
      /* ignore */
    }
  }, [composerDisplayValue, listening]);

  const handleMicClick = async () => {
    if (!browserSupportsSpeechRecognition || saving || composerLocked) return;
    if (listening) {
      onVoiceEvent?.('voice_commit');
      if (speechCommitInFlightRef.current) return;
      speechCommitInFlightRef.current = true;
      try {
        const next = joinPrefixWithTranscript(speechPrefixRef.current, transcriptRef.current);
        await SpeechRecognition.stopListening();
        resetTranscript();
        setInputValue(next);
      } finally {
        speechCommitInFlightRef.current = false;
      }
      return;
    }
    try {
      onVoiceEvent?.('voice_start');
      speechPrefixRef.current = inputValue;
      resetTranscript();
      await SpeechRecognition.startListening({ continuous: true, language: 'en-US' });
    } catch (err) {
      logger.error('Speech recognition start failed:', err);
      onVoiceEvent?.('voice_start_failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || composerLocked) return;
    let textForSubmit = inputValue;
    if (listening) {
      textForSubmit = joinPrefixWithTranscript(speechPrefixRef.current, transcriptRef.current);
      await SpeechRecognition.stopListening();
      resetTranscript();
      setInputValue(textForSubmit);
    }
    const text = textForSubmit.replace(/\s+$/, '').trim();
    if (!text) return;
    setInputValue('');
    speechPrefixRef.current = '';
    focusInput();
    onSubmitText(text);
  };

  if (showFinalScreen) {
    return (
      <div className={cx(styles['v2-input-area'], styles['v2-input-area--results'])}>
        <div className={styles['v2-results-hint']}>
          Quick check: review the <strong>Financial Profile</strong> on the right, then tap <strong>View Results</strong>.
        </div>
        <button
          type="button"
          className={styles['v2-view-results-button']}
          onClick={onViewResults}
          disabled={saving}
        >
          View Results
        </button>
      </div>
    );
  }

  return (
    <div className={styles['v2-input-area']}>
      <button
        type="button"
        className={cx(styles['v2-mic-button'], listening && styles['v2-mic-button--active'])}
        aria-label={listening ? 'Stop voice input' : 'Start voice input'}
        aria-pressed={listening}
        title={
          !browserSupportsSpeechRecognition
            ? 'Voice input is not supported in this browser'
            : listening
              ? 'Listening — click to stop and keep text'
              : 'Click to speak — your words appear in the answer field'
        }
        disabled={!browserSupportsSpeechRecognition || saving || composerLocked}
        onClick={handleMicClick}
      >
        {listening ? <ListeningIcon /> : <MicIcon />}
      </button>
      <form
        className={cx(styles['v2-input-form'], styles['v2-input-area-inner'])}
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <textarea
          ref={composerInputRef}
          value={composerDisplayValue}
          onChange={(ev) => {
            if (listening) return;
            setInputValue(ev.target.value.slice(0, INPUT_MAX_LEN));
          }}
          onInput={(ev) => {
            try {
              ev.target.style.height = 'auto';
              ev.target.style.height = `${ev.target.scrollHeight}px`;
            } catch {
              /* ignore */
            }
          }}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' && !listening) {
              ev.preventDefault();
              ev.currentTarget.form?.requestSubmit();
            }
          }}
          readOnly={listening}
          placeholder={listening ? 'Listening… speak now' : 'Type your answer…'}
          className={cx(styles['text-input'], styles['text-input--v2'], styles['textarea--v2'])}
          disabled={composerLocked}
          maxLength={INPUT_MAX_LEN}
          aria-label="Your answer"
          name="v2-chat-reply"
          enterKeyHint="send"
          inputMode="text"
          autoComplete="off"
          autoCorrect="on"
          autoCapitalize="sentences"
          spellCheck
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          rows={1}
        />
        <button
          type="submit"
          className={styles['v2-send-button']}
          disabled={!canSendComposer}
          aria-label="Send"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </form>
    </div>
  );
});

/** Tab on the right edge of the chat; the profile panel slides open/closed to this button. */
export function ProfileHandleButton({ open, onClick, onTouchStart, onTouchEnd }) {
  return (
    <button
      type="button"
      className={cx(
        styles['v2-mobile-profile-handle'],
        open && styles['v2-mobile-profile-handle--open']
      )}
      aria-label={open ? 'Hide profile panel' : 'Show profile panel'}
      aria-expanded={open}
      onClick={onClick}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <span className={styles['v2-profile-handle-chevron']} aria-hidden>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 6l-6 6 6 6" />
        </svg>
      </span>
      <span key={open ? 'hide' : 'show'} className={styles['v2-profile-handle-label']}>
        {open ? 'Hide Profile' : 'Profile'}
      </span>
    </button>
  );
}

/**
 * Sliding financial profile. `showChrome` is the edge handle and dimmed backdrop
 * used when the panel is a drawer (copilot and embedded Finance Q&A).
 */
export function ProfileDrawer({
  open,
  showChrome = true,
  onClose,
  onToggle,
  onHandleTouchStart,
  onHandleTouchEnd,
  onDrawerTouchStart,
  onDrawerTouchEnd,
  children,
}) {
  return (
    <>
      {showChrome ? (
        <div
          className={cx(styles['v2-mobile-profile-backdrop'], open && styles['v2-mobile-profile-backdrop--open'])}
          aria-hidden={!open}
          onClick={onClose}
        />
      ) : null}
      <div
        className={styles['v2-mobile-profile-drawer']}
        onTouchStart={onDrawerTouchStart}
        onTouchEnd={onDrawerTouchEnd}
      >
        {showChrome ? (
          <ProfileHandleButton
            open={open}
            onClick={onToggle}
            onTouchStart={onHandleTouchStart}
            onTouchEnd={onHandleTouchEnd}
          />
        ) : null}
        <div
          className={styles['v2-mobile-profile-drawer-body']}
          inert={showChrome && !open ? true : undefined}
        >
          {children}
        </div>
      </div>
    </>
  );
}
